import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { cartStore } from '@/entities/cart/store/cartStore';
import { OrderApi } from '@/entities/order/api';
import type { StatusEnum } from '@/entities/order/model';
import { orderStore } from '@/entities/order/store/orderStore';
import {
  isQueryFlagTrue,
  ORDER_QUERY_PARAMS,
  ORDER_TIMEOUTS,
} from '@/shared/constants/orderConstants';
import { uiStore } from '@/shared/stores/uiStore';

export function usePaymentFlow() {
  const { search } = useLocation();
  const navigate = useNavigate();

  const {
    orderStatusModalVisible,
    openOrderStatusModal,
    closeOrderStatusModal,
    orderStatusModalSelectedOrder,
  } = orderStore;
  const [visibleFailModal, setVisibleFailModal] = useState(false);
  const [closedByUser, setClosedByUser] = useState(false);
  const [orderIdForPolling, setOrderIdForPolling] = useState<number | null>(
    null
  );

  useEffect(() => {
    setClosedByUser(false);
    const sp = new URLSearchParams(search);
    const successFlag = isQueryFlagTrue(sp, ORDER_QUERY_PARAMS.paymentSuccess);
    const orderIdStr = sp.get(ORDER_QUERY_PARAMS.orderId);

    if (successFlag && orderIdStr) {
      const id = Number(orderIdStr);

      OrderApi.confirmPayment(id).catch(() => {});

      setOrderIdForPolling(id);

      sp.delete(ORDER_QUERY_PARAMS.paymentSuccess);
      sp.delete(ORDER_QUERY_PARAMS.orderId);
      navigate(
        { search: sp.toString() ? `?${sp.toString()}` : '' },
        { replace: true }
      );
    }
  }, [search, navigate]);

  useEffect(() => {
    if (!orderIdForPolling) return;

    let tries = 0;

    const loadOrder = async (onFinished: () => void) => {
      const detail = await orderStore.pollDetails(orderIdForPolling);
      if (!detail) return;

      const status = detail.status as StatusEnum | undefined;

      if (status === 'PAID') {
        uiStore.closeCart();
        cartStore.setItems([]);
        if (!closedByUser) {
          openOrderStatusModal(orderIdForPolling);
        }
        return;
      }

      if (status === 'ACCEPTED') {
        if (!closedByUser) {
          openOrderStatusModal(orderIdForPolling);
        }
        onFinished();
        return;
      }

      if (status === 'FAILED') {
        uiStore.closeCart();
        setVisibleFailModal(true);
        onFinished();
      }
    };

    loadOrder(() => {});

    const timerId: ReturnType<typeof setInterval> = setInterval(async () => {
      tries += 1;
      try {
        await loadOrder(() => clearInterval(timerId));
      } finally {
        if (tries > ORDER_TIMEOUTS.pollMaxTries) {
          clearInterval(timerId);
        }
      }
    }, ORDER_TIMEOUTS.pollIntervalMs);

    return () => {
      clearInterval(timerId);
    };
  }, [closedByUser, openOrderStatusModal, orderIdForPolling]);

  return {
    closeOrderStatusModal: () => {
      closeOrderStatusModal();
      setClosedByUser(true);
    },
    visibleFailModal,
    orderStatusModalSelectedOrder: orderStatusModalSelectedOrder.value,
    orderStatusModalVisible: orderStatusModalVisible.value,
    closeFailModal: () => setVisibleFailModal(false),
  };
}
